import clsx from 'clsx';
import { getOpacity } from '@quno/patient-journey/src/utils/opacity';
import { backgroundColorClass } from '@quno/patient-journey/src/theme/backgroundColorClass';
import { useEffect, useState } from 'react';
import { getRichTextMultilocale } from '@quno/onboarding/src/generator/configs/componentMapper';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import {
  defaultLocale,
  type FormLocales,
} from '@quno/onboarding/src/constants/locales';
import { richTextLineBreakConfig } from '../../contentful/patient-journey/richTextLineBreakConfig';
import styles from './Footer.module.scss';
import type { Document } from '@contentful/rich-text-types';
import type { StringAttribute } from '@quno/onboarding/src/generator/types/generics/ModelAttributes';
import type { BackgroundColor } from '@quno/patient-journey/src/theme/backgroundColorClass';

export type FooterProps = {
  backgroundColor?: BackgroundColor;
  backgroundColorOpacity?: string;
  backgroundColorOverwrite?: string;
  fontColorOverride?: string | null;
  footerAddress: StringAttribute;
  footerContact: StringAttribute;
  locale: FormLocales;
};

export const Footer = ({
  backgroundColor = 'primary',
  backgroundColorOpacity,
  backgroundColorOverwrite,
  fontColorOverride,
  footerAddress,
  footerContact,
  locale,
}: FooterProps): JSX.Element => {
  const opacity = getOpacity(backgroundColorOpacity!);
  const [footerAddressContent, setFooterAddressContent] = useState<Document>();
  const [footerContactContent, setFooterContactContent] = useState<Document>();

  useEffect(() => {
    const run = async (): Promise<void> => {
      const [addressContent, contactContact] = await Promise.all([
        getRichTextMultilocale(footerAddress, locale),
        getRichTextMultilocale(footerContact, locale),
      ]);
      setFooterAddressContent(addressContent);
      setFooterContactContent(contactContact);
    };

    void run();
  }, [footerAddress, locale]);

  return (
    <footer
      className={styles.footer}
      style={{ color: fontColorOverride || 'black' }}
    >
      <div
        className={clsx(
          styles.backgroundLayer,
          backgroundColorClass(backgroundColor),
        )}
        style={{
          backgroundColor: backgroundColorOverwrite || undefined,
          opacity: opacity || '1',
        }}
      />

      <div className={clsx(styles.footerContent)}>
        {!!footerAddress?.[defaultLocale] && (
          <div className={styles.address}>
            {footerAddressContent && (
              <div>
                {documentToReactComponents(
                  footerAddressContent,
                  richTextLineBreakConfig,
                )}
              </div>
            )}
          </div>
        )}
        {!!footerContact?.[defaultLocale] && footerContactContent && (
          <div className={styles.contactNumber}>
            {documentToReactComponents(
              footerContactContent,
              richTextLineBreakConfig,
            )}
          </div>
        )}
      </div>
    </footer>
  );
};
